import { useSuspenseInfiniteQuery } from '@tanstack/react-query';
import api from '@/axios';
import { GET_LOT_SIZING_LINES_KEY } from '@/keys';
import type { LotSizingLine } from '@/types/core/lot-sizing/lot-sizing-line';
import type { Paginated } from '@/types/core/paginated';

export async function fetchLotSizingLines(
  lotSizingId: string,
  supplierId: string,
  page: number,
): Promise<
  Paginated<LotSizingLine> & {
    isEligibleToUpdate: boolean;
  }
> {
  const { data } = await api.get<
    Paginated<LotSizingLine> & {
      isEligibleToUpdate: boolean;
    }
  >(`/lot-sizings/${lotSizingId}/lines`, {
    params: {
      page,
      perPage: PER_PAGE_DEFAULT,
      filters: JSON.stringify([
        {
          id: 'supplierId',
          value: Number(supplierId),
          operator: 'eq' as const,
        },
      ]),
    },
  });
  return data;
}

const PER_PAGE_DEFAULT = 10;

export const getLotSizingLinesBySupplierInfiniteQueryKey = (
  lotSizingId: string,
  supplierId: string,
) =>
  [
    ...GET_LOT_SIZING_LINES_KEY,
    lotSizingId,
    supplierId,
    PER_PAGE_DEFAULT,
  ] as const;

export function useLotSizingLinesBySupplier(
  lotSizingId: string,
  supplierId: string,
) {
  return useSuspenseInfiniteQuery({
    queryKey: getLotSizingLinesBySupplierInfiniteQueryKey(
      lotSizingId,
      supplierId,
    ),
    queryFn: ({ pageParam }) =>
      fetchLotSizingLines(lotSizingId, supplierId, pageParam),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.currentPage < lastPage.maxPage
        ? lastPage.currentPage + 1
        : undefined,
  });
}
